import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getDepartmentTranslation, addDepartmentTranslation, saveTranslationsToFile } from '../../services/departmentsTranslationsService';
import { getInstitutionTranslation } from '../../services/institutionsTranslationsService';
import { getDistrictTranslation } from '../../services/districtsTranslationsService';
import { getAcademyTranslation } from '../../services/academiesTranslationsService';
import { getRegionTranslation } from '../../services/regionsTranslationsService';
import { getCountryTranslation } from '../../services/countriesTranslationsService';
import { 
  FolderIcon, 
  SearchIcon, 
  XCircleIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeIcon,
  BuildingIcon,
  FileTextIcon
} from 'lucide-react';

const Departments = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  
  // State management
  const [departments, setDepartments] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [institutionsLoading, setInstitutionsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const LIMIT_OPTIONS = [10, 20, 50, 100];
  const [institutionId, setInstitutionId] = useState('');
  
  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newDepartment, setNewDepartment] = useState({
    institutionId: '',
    code: '',
    description: '',
    translations: {
      ar: '',
      fr: '',
      en: ''
    }
  });
  const [formErrors, setFormErrors] = useState({});

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState({
    id: '',
    name: '',
    code: '',
    description: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // Fetch departments - API: /departments or /institutions/{id}/departments
  const fetchDepartments = async (page = 1, search = '', filterInstitutionId = institutionId, itemsPerPage = limit) => {
    setLoading(true);
    try {
      let url = '/organization/departments';
      if (filterInstitutionId) url = `/organization/institutions/${filterInstitutionId}/departments`;
      const params = new URLSearchParams({ page: page.toString(), limit: itemsPerPage.toString() });
      if (search.trim()) params.set('search', search.trim());
      const response = await api.get(`${url}?${params.toString()}`);
      
      if (response.data.success) {
        setDepartments(response.data.data.departments || []);
        setTotal(response.data.data.pagination.total || 0);
        setCurrentPage(response.data.data.pagination.page || 1);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error(t('departments.fetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // Fetch institutions
  const fetchInstitutions = async () => {
    setInstitutionsLoading(true);
    try {
      let allInstitutions = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await api.get(`/organization/institutions?page=${currentPage}&limit=100`);
        if (response.data.success) {
          const fetchedInstitutions = response.data.data.institutions || [];
          allInstitutions = [...allInstitutions, ...fetchedInstitutions];
          
          const pagination = response.data.data.pagination;
          if (pagination && currentPage < pagination.pages) {
            currentPage++;
          } else {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      
      setInstitutions(allInstitutions);
    } catch (error) {
      console.error('Error fetching institutions:', error);
      toast.error(t('departments.institutionsFetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setInstitutionsLoading(false);
    }
  };

  useEffect(() => { fetchInstitutions(); }, []);

  useEffect(() => {
    if (searchTerm === '') {
      fetchDepartments(1, '', institutionId);
      return;
    }
    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      fetchDepartments(1, searchTerm, institutionId).finally(() => setIsSearching(false));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, institutionId]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchDepartments(1, searchTerm, institutionId);
  };

  // Translate names
  const translateDepartmentName = (name, language) => {
    return getDepartmentTranslation(name, language);
  };

  const translateInstitutionName = (name, language) => {
    return getInstitutionTranslation(name, language);
  };

  const translateDistrictName = (name, language) => {
    return getDistrictTranslation(name, language);
  };

  const translateAcademyName = (name, language) => {
    return getAcademyTranslation(name, language);
  };

  const translateRegionName = (name, language) => {
    return getRegionTranslation(name, language);
  };

  const translateCountryName = (name, language) => {
    return getCountryTranslation(name, language);
  };

  const clearFilters = () => {
    setInstitutionId('');
    setSearchTerm('');
    setCurrentPage(1);
    fetchDepartments(1, '', '', limit);
  };

  const getActiveFiltersCount = () => (institutionId ? 1 : 0);

  const didMountDepartments = useRef(false);
  useEffect(() => {
    if (!didMountDepartments.current) { didMountDepartments.current = true; return; }
    setCurrentPage(1);
    fetchDepartments(1, searchTerm, institutionId, limit);
  }, [institutionId, limit]);

  // Handle export translations
  const handleExportTranslations = () => {
    const result = saveTranslationsToFile();
    if (result.success) {
      toast.success(result.message, {
        duration: 5000,
        position: 'top-center'
      });
    } else {
      toast.error(result.message, {
        duration: 3000,
        position: 'top-center'
      });
    }
  };

  // Open add modal
  const openAddModal = () => {
    setNewDepartment({
      institutionId: '',
      code: '',
      description: '',
      translations: {
        ar: '',
        fr: '',
        en: ''
      }
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setNewDepartment({
      institutionId: '',
      code: '',
      description: '',
      translations: {
        ar: '',
        fr: '',
        en: ''
      }
    });
    setFormErrors({});
  };

  // Add department
  const addDepartment = async () => {
    const errors = {};
    if (!newDepartment.institutionId) {
      errors.institutionId = t('departments.institutionRequired');
    }
    if (!newDepartment.code.trim()) {
      errors.code = t('departments.codeRequired');
    } else if (newDepartment.code.trim().length < 2 || newDepartment.code.trim().length > 15) {
      errors.code = t('departments.codeLength');
    }
    if (!newDepartment.description.trim()) {
      errors.description = t('departments.descriptionRequired');
    }
    if (!newDepartment.translations.ar.trim()) {
      errors.translation_ar = t('departments.nameRequired_ar');
    }
    if (!newDepartment.translations.fr.trim()) {
      errors.translation_fr = t('departments.nameRequired_fr');
    }
    if (!newDepartment.translations.en.trim()) {
      errors.translation_en = t('departments.nameRequired_en');
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsAdding(true);
    try {
      const requestData = {
        institutionId: parseInt(newDepartment.institutionId),
        name: newDepartment.translations.ar.trim(),
        code: newDepartment.code.trim(),
        description: newDepartment.description.trim()
      };
      
      const response = await api.post('/organization/departments', requestData);
      
      if (response.data.success) {
        const translationResult = addDepartmentTranslation(
          newDepartment.translations.ar.trim(),
          {
            ar: newDepartment.translations.ar.trim(),
            fr: newDepartment.translations.fr.trim(),
            en: newDepartment.translations.en.trim()
          }
        );

        if (translationResult.success) {
          console.log('Translation saved successfully:', translationResult.message);
        }
        
        toast.success(response.data.message || t('departments.addSuccess'), { duration: 3000, position: 'top-center' });
        closeAddModal();
        fetchDepartments(currentPage, searchTerm, institutionId);
      }
    } catch (error) {
      console.error('Error adding department:', error);
      const errorMessage = error.response?.data?.message || error.message || t('departments.addError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsAdding(false);
    }
  };

  // Handle edit department
  const handleEditDepartment = (department) => {
    setEditingDepartment({
      id: department.id,
      name: department.name,
      code: department.code,
      description: department.description || ''
    });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  // Update department
  const updateDepartment = async () => {
    const errors = {};
    if (!editingDepartment.name.trim()) {
      errors.name = t('departments.nameRequired');
    }
    if (!editingDepartment.code.trim()) {
      errors.code = t('departments.codeRequired');
    } else if (editingDepartment.code.trim().length < 2 || editingDepartment.code.trim().length > 15) {
      errors.code = t('departments.codeLength');
    }
    if (!editingDepartment.description.trim()) {
      errors.description = t('departments.descriptionRequired');
    }
    
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsEditing(true);
    try {
      const requestData = {
        name: editingDepartment.name.trim(),
        code: editingDepartment.code.trim(),
        description: editingDepartment.description.trim()
      };
      
      const response = await api.put(`/organization/departments/${editingDepartment.id}`, requestData);
      
      if (response.data.success) {
        toast.success(response.data.message || t('departments.updateSuccess'), { duration: 3000, position: 'top-center' });
        setShowEditModal(false);
        setEditingDepartment({
          id: '',
          name: '',
          code: '',
          description: ''
        });
        setEditFormErrors({});
        fetchDepartments(currentPage, searchTerm, institutionId);
      }
    } catch (error) {
      console.error('Error updating department:', error);
      const errorMessage = error.response?.data?.message || error.message || t('departments.updateError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsEditing(false);
    }
  };

  // Delete department
  const deleteDepartment = async (departmentId, departmentName) => {
    if (window.confirm(t('departments.deleteConfirm', { name: departmentName }))) {
      try {
        const response = await api.delete(`/organization/departments/${departmentId}`);
        
        if (response.data.success) {
          toast.success(response.data.message || t('departments.deleteSuccess'), { duration: 3000, position: 'top-center' });
          fetchDepartments(currentPage, searchTerm, institutionId);
        }
      } catch (error) {
        console.error('Error deleting department:', error);
        const errorMessage = error.response?.data?.message || error.message || t('departments.deleteError');
        toast.error(errorMessage, { duration: 3000, position: 'top-center' });
      }
    }
  };

  // Pagination
  const totalPages = Math.ceil(total / limit);
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchDepartments(page, searchTerm, institutionId);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-primary-100 rounded-lg">
                <FolderIcon className="h-6 w-6 text-primary-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('departments.title')}
              </h1>
            </div>
            <p className="text-gray-600">{t('departments.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={openAddModal}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              {t('departments.addDepartment')}
            </button>
            <button
              onClick={handleExportTranslations}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <GlobeIcon className="h-5 w-5" />
              {t('departments.exportTranslations')}
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters - Modern Design */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <form onSubmit={handleSearch} className="p-4">
          <div className={`flex flex-col lg:flex-row gap-4 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('common.search')}</label>
              <div className="relative">
                <SearchIcon className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <input type="text" placeholder={t('departments.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors`} />
                {isSearching && <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}><div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" /></div>}
              </div>
            </div>
            <div className="lg:w-48">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('departments.institution')}</label>
              <select value={institutionId} onChange={(e) => setInstitutionId(e.target.value)} disabled={institutionsLoading} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                <option value="">{institutionsLoading ? t('departments.loadingInstitutions') : t('departments.allInstitutions')}</option>
                {institutions.map((institution) => <option key={institution.id} value={institution.id}>{translateInstitutionName(institution.name, currentLanguage || 'fr')}</option>)}
              </select>
            </div>
            <div className="lg:w-32">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.perPage')}</label>
              <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                {LIMIT_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2 lg:flex-shrink-0">
              <button type="submit" className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors">{t('common.search')}</button>
              {(institutionId || searchTerm) && (
                <button type="button" onClick={clearFilters} title={t('common.clearFilters')} className="p-2.5 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"><XCircleIcon className="h-5 w-5" /></button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Departments Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('departments.department')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('departments.code')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('departments.institution')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('departments.status')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('departments.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <span className="ml-2 text-gray-600">{t('common.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : departments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    {t('departments.noDepartments')}
                  </td>
                </tr>
              ) : (
                departments.map((department) => (
                  <tr key={department.id} className="hover:bg-gray-50">
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <FolderIcon className="h-4 w-4 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {translateDepartmentName(department.name, currentLanguage || 'fr')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <FileTextIcon className="h-3 w-3 mr-1" />
                        {department.code}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="text-sm text-gray-900">
                        {translateInstitutionName(department.institution_name, currentLanguage || 'fr')}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        department.is_active === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {department.is_active === 1 ? t('departments.active') : t('departments.inactive')}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditDepartment(department)}
                          className="text-primary-600 hover:text-primary-900"
                          title={t('departments.edit')}
                        >
                          <EditIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteDepartment(department.id, translateDepartmentName(department.name, currentLanguage || 'fr'))}
                          className="text-red-600 hover:text-red-900"
                          title={t('departments.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && departments.length > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                  currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t('departments.previous')}
              </button>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                  currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t('departments.next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('departments.showing')} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {t('departments.to')}{' '}
                  <span className="font-medium">{Math.min(currentPage * limit, total)}</span> {t('departments.of')}{' '}
                  <span className="font-medium">{total}</span> {t('departments.results')}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                      currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span
                          key={page}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                      currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Department Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('departments.addDepartment')}</h3>
              <button onClick={closeAddModal} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('departments.institution')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={newDepartment.institutionId}
                  onChange={(e) => setNewDepartment(prev => ({ ...prev, institutionId: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.institutionId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={institutionsLoading}
                >
                  <option value="">{institutionsLoading ? t('departments.loadingInstitutions') : t('departments.selectInstitution')}</option>
                  {institutions.map((institution) => (
                    <option key={institution.id} value={institution.id}>
                      {translateInstitutionName(institution.name, currentLanguage || 'fr')}
                    </option>
                  ))}
                </select>
                {formErrors.institutionId && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.institutionId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('departments.departmentCode')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newDepartment.code}
                  onChange={(e) => setNewDepartment(prev => ({ ...prev, code: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.code ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="TD001"
                />
                {formErrors.code && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.code}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('departments.description')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newDepartment.description}
                  onChange={(e) => setNewDepartment(prev => ({ ...prev, description: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  rows="3"
                />
                {formErrors.description && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">{t('departments.translations')}</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('departments.nameFr')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newDepartment.translations.fr}
                      onChange={(e) => setNewDepartment(prev => ({
                        ...prev,
                        translations: { ...prev.translations, fr: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.translation_fr ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.translation_fr && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.translation_fr}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('departments.nameEn')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newDepartment.translations.en}
                      onChange={(e) => setNewDepartment(prev => ({
                        ...prev,
                        translations: { ...prev.translations, en: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.translation_en ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.translation_en && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.translation_en}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('departments.nameAr')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newDepartment.translations.ar}
                      onChange={(e) => setNewDepartment(prev => ({
                        ...prev,
                        translations: { ...prev.translations, ar: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.translation_ar ? 'border-red-500' : 'border-gray-300'
                      }`}
                      dir="rtl"
                    />
                    {formErrors.translation_ar && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.translation_ar}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeAddModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isAdding}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={addDepartment}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                disabled={isAdding}
              >
                {isAdding ? t('departments.adding') : t('departments.addDepartment')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Department Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('departments.editDepartment')}</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('departments.departmentName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingDepartment.name}
                  onChange={(e) => setEditingDepartment(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editFormErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('departments.departmentCode')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingDepartment.code}
                  onChange={(e) => setEditingDepartment(prev => ({ ...prev, code: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.code ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editFormErrors.code && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.code}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('departments.description')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editingDepartment.description}
                  onChange={(e) => setEditingDepartment(prev => ({ ...prev, description: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  rows="3"
                />
                {editFormErrors.description && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.description}</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isEditing}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={updateDepartment}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                disabled={isEditing}
              >
                {isEditing ? t('departments.updating') : t('departments.update')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Departments;
